import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { PaymentService } from '../services/payment.service';
import { mapTo, switchMap, tap } from 'rxjs/operators';
import { PaymentStateEnum } from '../models/payment-state.enum';
import { PblLiabilityPaymentStatusEnum } from '../models/pbl-payment-status.enum';
import { LogRocketService } from '../services/log-rocket.service';
import { LinkParamsService } from '../services/link-params.service';
import { PaymentMethod } from '../models/payment-method.model';
import { PaymentMethodMapper } from '../models/payment-type.model';
import { IntegrationVersionEnum } from '../models/liability-data.model';
import { TitleService } from '../services/title.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RedirectGuard implements CanActivate {
  constructor(
    private paymentService: PaymentService,
    private linkParamsService: LinkParamsService,
    private router: Router,
    private logRocketService: LogRocketService,
    private titleService: TitleService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    const token = next.paramMap.get('token');
    const { name, surname, email, type, connectApp, paymentMethod, methodSelection } = next.queryParams;
    let linkParams = { name, surname, email, type, connectApp };

    if (environment.maintenance) {
      this.router.navigate(['/maintenance']);
    }

    if (linkParams.surname) {
      this.linkParamsService.storeLinkParams(linkParams);
    } else {
      linkParams = this.linkParamsService.linkParams;
    }

    this.paymentService.token = token;

    return this.paymentService.getPayment(token).pipe(
      tap(data => {
        this.titleService.setTitle(data);
        if (data.email) {
          this.logRocketService.identify(data.email, data.customer);
        }
      }),
      switchMap(data => {
        if (type === 'connect') {
          this.paymentService.redirectToBillGatewayApp({
            token,
            paymentType: PaymentMethodMapper.fromParam(type),
            connectApp
          });
        } else if (
          methodSelection === 'direct' &&
          !!this.paymentService.payment.surname &&
          !!this.paymentService.payment.email
        ) {
          return this.direct(paymentMethod, type);
        } else if (data.paymentStatus === PblLiabilityPaymentStatusEnum.CANCELLED) {
          return of(this.samePath(next, [token, 'cancelled']) || this.router.createUrlTree([token, 'cancelled']));
        } else if (data.paymentStatus === PblLiabilityPaymentStatusEnum.CANCELLED_PAID) {
          return of(
            this.samePath(next, [token, 'cancelled-paid']) || this.router.createUrlTree([token, 'cancelled-paid'])
          );
        } else if (data.paymentStatus === PblLiabilityPaymentStatusEnum.PAID) {
          return of(this.samePath(next, [token, 'result']) || this.router.createUrlTree([token, 'result']));
        } else if (data.paymentStatus === PblLiabilityPaymentStatusEnum.PENDING_PAYMENT) {
          return of(this.samePath(next, [token, 'pending']) || this.router.createUrlTree([token, 'pending']));
        } else if (!data.customer && data.integrationVersion === IntegrationVersionEnum.V0 && !linkParams.surname) {
          console.error('Broken link - no customer data');
          return of(this.samePath(next, ['/error']) || this.router.createUrlTree(['/error']));
        } else if (
          data.paymentState !== PaymentStateEnum.FAILED &&
          next.url.map(segment => segment.path).includes('result')
        ) {
          return of(this.samePath(next, [token]) || this.router.createUrlTree([token]));
        } else {
          return of(true);
        }
      })
    );
  }

  private samePath(next: ActivatedRouteSnapshot, redirectPath: string[]) {
    const nextPath = next.url.toString();
    const pathCheck = redirectPath.join(',');
    return nextPath === pathCheck;
  }

  private direct(paymentMethod: PaymentMethodParam, type: string) {
    const paymentType = PaymentMethodMapper.fromParam(type);

    switch (paymentMethod) {
      case 'blik':
        return this.paymentService
          .createImojePayment(
            PaymentMethod.BLIK,
            this.paymentService.payment.name,
            this.paymentService.payment.surname,
            this.paymentService.payment.email,
            paymentType
          )
          .pipe(mapTo(false));
      case 'transfer':
        return this.paymentService
          .createImojePayment(
            PaymentMethod.TRANSFER,
            this.paymentService.payment.name,
            this.paymentService.payment.surname,
            this.paymentService.payment.email,
            paymentType
          )
          .pipe(mapTo(false));
      case 'defer':
      case 'card':
      case 'MU':
        this.paymentService.redirectToBillGatewayApp({ paymentType });
        return of(false);
    }
  }
}

type PaymentMethodParam = 'auto' | 'banks' | 'apps' | 'defer' | 'blik' | 'card' | 'transfer' | 'MU';
